import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { FC, useState, useCallback } from 'react';
import { Noop } from '../../../extViewmodels/Utils';
import { useAdvancedFiltersContext } from '../../components-v2/AdvancedFilters/AdvancedFiltersContext';
import { baseStyleSheet, bs } from '../../styles/styles';
import { Checkbox } from '../Checkbox';
import { CustomDateRange } from '../CustomDateRange';
import { TagListBuilder } from '../entities/tags/TagListBuilder';
import { OwnershipFilterDropdown } from '../OwnershipFilterDropdown';
import { InfoIcon } from '../svgs/icons/InfoIcon';
import { TagExpandIcon } from '../svgs/icons/TagExpandIcon';
import { TagNarrowIcon } from '../svgs/icons/TagNarrowIcon';
import { PopoverType, TinyPopover } from '../TinyPopover';
import { CompanyTagFiltersPanel } from './CompanyTagFiltersPanel';
import { LastCommunicationDateDropdown } from './CompoundFiltersPanel/LastCommunicationDateDropdown';
import { CustomFieldFiltersPanel, useHasEligibleContactCustomFields } from './CustomFieldFiltersPanel';
import { KeyFactFiltersDropdown } from './KeyFactFilters';
import { LocationFiltersPanel } from './LocationFiltersPanel';
import { PopularTagsGeneric } from './PopularTagsGeneric';
import { SavedSearchesList } from './SavedSearchesList';
import { StatusFilterCheckboxes } from './StatusFilters';
import { styleSheet } from './styles';

interface AdvancedFiltersProps {
	onDismissFlyout: () => void;
	showPopularTags?: boolean;
	showSavedSearches?: boolean;
	savedSearchesReadOnly?: boolean;
	showHousehold?: boolean;
	showOwnershipFilters?: boolean;
	showStatusFilters?: boolean;
	showLocationFilters?: boolean;
	showCompanyTagFilters?: boolean;
	showCustomFields?: boolean;
	showLastCommunicationDate?: boolean;
	showKeyFactFilters?: boolean;
	showCreateDateFilters?: boolean;
	disableOwnershipFilters?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	showOwnedByOption?: boolean;
}

/**
 *
 * @param impersonationContext if passed here, also pass to the AdvancedFiltersContextProvider
 *
 */
export const AdvancedFiltersLegacyUI: FC<AdvancedFiltersProps> = ({
	onDismissFlyout,
	showPopularTags = false,
	showSavedSearches = false,
	savedSearchesReadOnly = true,
	showHousehold = false,
	showOwnershipFilters = false,
	showStatusFilters = false,
	showLocationFilters = false,
	showCompanyTagFilters = false,
	showCustomFields = false,
	showLastCommunicationDate = false,
	showKeyFactFilters = false,
	showCreateDateFilters = false,
	disableOwnershipFilters = false,
	impersonationContext,
	showOwnedByOption = false,
}) => {
	const [householdPopoverOpen, setHouseholdPopoverOpen] = useState(false);
	const [selectedSavedSearchId, setSelectedSavedSearchId] = useState<string | undefined>(undefined);

	const {
		createDateFilters,
		lastCommunicationDateFilters,
		keyFactFilters,
		statusFilters,
		tagFilters,
		locationFilters,
		companyTagFilters,
		customFieldFilters,
		saveChanges,
		onSave,
		householdGroupingOn,
		setHouseholdGroupingOn,
		ownershipFilter,
		selectedUserId,
		setOwnershipFilter,
		setSelectedUserId,
	} = useAdvancedFiltersContext();

	const onUserSelected = (userId: string) => {
		setSelectedUserId(userId);
		setOwnershipFilter({
			criteria: { property: Api.ContactFilterCriteriaProperty.OwnedBy, value: userId },
		});
	};

	const handleLoadSavedSearch = (newFilter: Api.IBulkContactsRequest) => {
		// Saved searches can't be combined with other filters, so apply directly and close
		onSave(newFilter.filter.criteria, newFilter.groupByHousehold, newFilter.ownershipFilter);
	};

	const handleClearAllFilters = useCallback(() => {
		const emptyCriteria: Api.IContactFilterCriteria[] = [];
		// Clear all filter states
		tagFilters.refreshTags(emptyCriteria);
		statusFilters.refreshStatuses(emptyCriteria);
		keyFactFilters.refreshKeyFacts(emptyCriteria);
		createDateFilters.refreshDateFilter(emptyCriteria);
		lastCommunicationDateFilters.refreshDateFilter(emptyCriteria);
		locationFilters.clearLocationFilters();
		companyTagFilters.clearCompanyTagFilters();
		customFieldFilters.clearCustomFieldFilters();
		setSelectedSavedSearchId(undefined);
		setOwnershipFilter(undefined);
		setSelectedUserId(undefined);
		setHouseholdGroupingOn(false);
	}, [
		tagFilters,
		statusFilters,
		keyFactFilters,
		createDateFilters,
		lastCommunicationDateFilters,
		locationFilters,
		companyTagFilters,
		customFieldFilters,
		setOwnershipFilter,
		setSelectedUserId,
		setHouseholdGroupingOn,
	]);

	const hasEligibleCustomFields = useHasEligibleContactCustomFields(impersonationContext);

	return (
		<>
			<header className={css(styleSheet.header)}>
				<h3 className={css(styleSheet.headerText)}>Advanced Filters</h3>
			</header>

			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.body)}>
					<section>
						<h4 className={css(styleSheet.title)}>Advanced Filters</h4>
						<p className={css(styleSheet.subtitle)}>You can combine or filter by tags.</p>
					</section>

					{showPopularTags ? (
						<>
							<div className={css(styleSheet.separator)} />

							<section className={css(styleSheet.filterGroup)}>
								<h5 className={css(styleSheet.sectionHeader)}>Popular Tags</h5>
								<PopularTagsGeneric
									tags={tagFilters.orTags}
									onTagAdded={tagFilters.addOrTag}
									onTagRemoved={tagFilters.removeOrTag}
									impersonationContext={impersonationContext}
								/>
							</section>
						</>
					) : null}

					{showSavedSearches ? (
						<>
							<div className={css(styleSheet.separator)} />

							<section className={css(styleSheet.filterGroup)}>
								<h5 className={css(styleSheet.sectionHeader)}>Saved Searches</h5>
								<SavedSearchesList
									readOnly={savedSearchesReadOnly}
									impersonationContext={impersonationContext}
									onSelectSearch={handleLoadSavedSearch}
									selectedSearchId={selectedSavedSearchId}
									onSearchSelected={setSelectedSavedSearchId}
									showRadioButtons
								/>
							</section>
						</>
					) : null}

					<section className={css(styleSheet.filterGroup)}>
						<div className={css(styleSheet.tagSection)}>
							<figure>
								<TagExpandIcon />
							</figure>
							<div>
								<p className={css(styleSheet.tagSectionTitle)}>
									Included tags (this will <span className={css(bs.fontBold)}>expand</span> the list)
								</p>
							</div>
						</div>
						<TagListBuilder
							styles={[styleSheet.tagListBuilderContainer, styleSheet.indentedTagList]}
							inputId='add-or-tags'
							impersonationContext={impersonationContext}
							onTagAdded={tagFilters.addOrTag}
							onTagRemoved={tagFilters.removeOrTag}
							separator={Api.FilterOperator.Or}
							tags={tagFilters.orTags}
							openOnFocus={true}
						/>
					</section>

					<section className={css(styleSheet.filterGroup)}>
						<div className={css(styleSheet.tagSection)}>
							<figure>
								<TagNarrowIcon />
							</figure>
							<div>
								<div className={css(styleSheet.tagSectionTitle)}>
									<span className={css(baseStyleSheet.fontBold)}>Narrow</span> the list
								</div>
							</div>
						</div>

						<div className={css(styleSheet.indentedBody)}>
							<Checkbox
								checked={tagFilters.showingAndTags}
								className={css(
									styleSheet.tagCheckbox,
									tagFilters.showingAndTags ? styleSheet.tagCheckboxExpanded : null
								)}
								id='and-tags'
								onChange={tagFilters.handleAndCheckChange}
							>
								<span className={css(styleSheet.checkboxText)}>Contacts must also have these tags...</span>
							</Checkbox>
							{tagFilters.showingAndTags ? (
								<TagListBuilder
									styles={[styleSheet.tagListBuilderContainer]}
									inputId='add-and-tags'
									impersonationContext={impersonationContext}
									onTagAdded={tagFilters.addAndTag}
									onTagRemoved={tagFilters.removeAndTag}
									separator={Api.FilterOperator.And}
									tags={tagFilters.andTags}
									openOnFocus={true}
								/>
							) : null}
							<Checkbox
								checked={tagFilters.showingNotTags}
								className={css(
									styleSheet.tagCheckbox,
									tagFilters.showingNotTags ? styleSheet.tagCheckboxExpanded : null
								)}
								id='not-tags'
								onChange={tagFilters.handleNotCheckChange}
							>
								<span className={css(styleSheet.checkboxText)}>Exclude these tags...</span>
							</Checkbox>
							{tagFilters.showingNotTags ? (
								<TagListBuilder
									styles={[styleSheet.tagListBuilderContainer]}
									inputId='add-not-tags'
									impersonationContext={impersonationContext}
									onTagAdded={tagFilters.addNotTag}
									onTagRemoved={tagFilters.removeNotTag}
									separator={Api.FilterOperator.Not}
									tags={tagFilters.notTags}
									lockedTags={tagFilters.lockedNotTags}
									openOnFocus={true}
								/>
							) : null}
						</div>
					</section>

					{showHousehold ? (
						<>
							<div className={css(styleSheet.separator)} />

							<section className={css(styleSheet.filterGroup)}>
								<h5 className={css(styleSheet.sectionHeader)}>Households</h5>

								<div className={css(baseStyleSheet.horizontalStack)}>
									<Checkbox
										checked={householdGroupingOn}
										onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setHouseholdGroupingOn(ev.target.checked)}
										id='household-grouping-checkbox'
									>
										<span>Display recipients by household</span>
									</Checkbox>
									<div
										className={`${css(baseStyleSheet.truncateText)}`}
										onMouseEnter={() => setHouseholdPopoverOpen(true)}
										onMouseLeave={() => setHouseholdPopoverOpen(false)}
									>
										<TinyPopover
											anchor={<InfoIcon />}
											anchorStyles={[styleSheet.infoIconContainer]}
											isOpen={householdPopoverOpen}
											onRequestClose={() => setHouseholdPopoverOpen(false)}
											placement={['top', 'left']}
											dismissOnOutsideAction={true}
											type={PopoverType.success}
										>
											<p className={css(styleSheet.householdHoverCard)}>
												<strong className={css(styleSheet.householdBold)}>Household: </strong> you can customize whether
												to send a group email or handwritten card to individuals or households.
											</p>
										</TinyPopover>
									</div>
								</div>
							</section>
						</>
					) : null}

					{showOwnershipFilters ? (
						<>
							<div className={css(styleSheet.separator)} />

							<section className={css(styleSheet.filterGroup)}>
								<h5 className={css(styleSheet.sectionHeader)}>Ownership</h5>
								<OwnershipFilterDropdown
									initialOwnershipFilter={ownershipFilter}
									onOwnershipFilterChanged={setOwnershipFilter}
									onSelectedUserChanged={onUserSelected}
									styles={[styleSheet.ownershipDropdown]}
									allowSelectAUserOption={true}
									selectedUserId={selectedUserId}
									disabled={disableOwnershipFilters}
									impersonationContext={impersonationContext}
									showOwnedByOption={showOwnedByOption}
								/>
							</section>
						</>
					) : null}
					{showStatusFilters ? (
						<>
							<div className={css(styleSheet.separator)} />

							<section className={css(styleSheet.filterGroup)}>
								<h5 className={css(styleSheet.sectionHeader)}>Status</h5>
								<StatusFilterCheckboxes
									styles={[styleSheet.statusFilters]}
									selectedStatuses={statusFilters.selectedStatuses}
									setSelectedStatuses={statusFilters.setSelectedStatuses}
								/>
							</section>
						</>
					) : null}

					{showLocationFilters ? (
						<>
							<div className={css(styleSheet.separator)} />

							<section className={css(styleSheet.filterGroup)}>
								<h5 className={css(styleSheet.sectionHeader)}>Location</h5>
								<LocationFiltersPanel />
							</section>
						</>
					) : null}

					{showCompanyTagFilters ? (
						<>
							<div className={css(styleSheet.separator)} />

							<section className={css(styleSheet.filterGroup)}>
								<h5 className={css(styleSheet.sectionHeader)}>Company Tags</h5>
								<CompanyTagFiltersPanel />
							</section>
						</>
					) : null}

					{showCustomFields && hasEligibleCustomFields ? (
						<>
							<div className={css(styleSheet.separator)} />

							<section className={css(styleSheet.filterGroup)}>
								<h5 className={css(styleSheet.sectionHeader)}>Custom Fields</h5>
								<CustomFieldFiltersPanel onSearch={() => saveChanges()} impersonationContext={impersonationContext} />
							</section>
						</>
					) : null}

					{showCreateDateFilters ? (
						<>
							<div className={css(styleSheet.separator)} />

							<section className={css(styleSheet.filterGroup)}>
								<h5 className={css(styleSheet.sectionHeader)}>Creation Date</h5>
								<CustomDateRange
									startDateRequired={false}
									forceSmallText
									defaultOpen={false}
									resetDates={() => createDateFilters.refreshDateFilter([])}
									from={createDateFilters.startDate || null}
									to={createDateFilters.endDate || null}
									onChange={(from, to) => {
										createDateFilters.setStartDate(from);
										if (to !== from) {
											createDateFilters.setEndDate(to);
										}
									}}
								/>
							</section>
						</>
					) : null}

					{showLastCommunicationDate ? (
						<>
							<div className={css(styleSheet.separator)} />

							<section className={css(styleSheet.filterGroup)}>
								<h5 className={css(styleSheet.sectionHeader)}>Last Communication</h5>
								<LastCommunicationDateDropdown dateRangeFilters={lastCommunicationDateFilters} saveChanges={Noop} />
							</section>
						</>
					) : null}

					{showKeyFactFilters ? (
						<>
							<div className={css(styleSheet.separator)} />

							<section className={css(styleSheet.filterGroup)}>
								<h5 className={css(styleSheet.sectionHeader)}>Key Fact</h5>
								<KeyFactFiltersDropdown
									selectedKeyFact={keyFactFilters.selectedKeyFact}
									setSelectedKeyFact={keyFactFilters.setSelectedKeyFact}
									selectedPolicies={keyFactFilters.selectedPolicies}
									setSelectedPolicies={keyFactFilters.setSelectedPolicies}
									selectedKeyFactStartDate={keyFactFilters.selectedKeyFactStartDate}
									setSelectedKeyFactStartDate={keyFactFilters.setSelectedKeyFactStartDate}
									selectedKeyFactEndDate={keyFactFilters.selectedKeyFactEndDate}
									setSelectedKeyFactEndDate={keyFactFilters.setSelectedKeyFactEndDate}
									selectedMinAge={keyFactFilters.selectedMinAge}
									setSelectedMinAge={keyFactFilters.setSelectedMinAge}
									selectedMaxAge={keyFactFilters.selectedMaxAge}
									setSelectedMaxAge={keyFactFilters.setSelectedMaxAge}
									keyFactErrors={keyFactFilters.keyFactErrors}
									impersonationContext={impersonationContext}
								/>
							</section>
						</>
					) : null}
				</div>
			</div>

			<footer className={css(styleSheet.footer)}>
				<div className={css(styleSheet.separator)} />

				<div className={css(styleSheet.clearAllContainer)}>
					<button className={css(baseStyleSheet.brandLink)} onClick={handleClearAllFilters}>
						Clear all filters
					</button>
				</div>

				<div className={css(styleSheet.actions)}>
					<button
						className={css(baseStyleSheet.ctaButton, styleSheet.button)}
						onClick={() => saveChanges()}
						disabled={keyFactFilters.keyFactErrors.length > 0}
					>
						Ok
					</button>

					<button className={css(baseStyleSheet.ctaButtonReverse, styleSheet.button)} onClick={onDismissFlyout}>
						Cancel
					</button>
				</div>
			</footer>
		</>
	);
};
